import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import Decimal from 'decimal.js-light';

import config from 'utils/config';
import Modal from 'components/ui/Modal';
import { CurrencyInput } from 'components/ui/Inputs';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { ActionButton, ConfirmButton } from 'components/ui/Button';
import AlertBox from 'components/ui/AlertBox';

import { OPERATION_TYPE } from '../ExtrasHotel.constants';

import {
  StyledButtonGroup,
  StyledErrorMessage,
  StyledFormFooter,
  StyledBreakdown,
  StyledBreakdownRow,
  StyledTotalContainer,
  StyledInputContainer,
  StyledInputWrapper,
  StyledLabel,
  StyledWarningMessage,
  StyledCurrencyValue
} from './ManageHotel.styles';

const enableHotelCompensation = config('ENABLE_HOTEL_COMPENSATION', { canOverride: true }) === 'true';

const ManageHotel = ({
  canBeCancelledOnThirdParty,
  date,
  jacTravelRef,
  jacTravelTotalPrice,
  jacTravelTotalCommission,
  hotelId,
  price,
  loading,
  currency,
  isConfirmed,
  onCancel,
  onExchange,
  canExchange,
  canCompensate,
  isCompensationDisabled,
  refundProposal,
  isPackage,
  isHotelOnly,
  refundProposalError,
  margin,
  isCompensated,
  isDotwBooking,
  isAlreadyCancelled,
  penaltiesApplied
}) => {
  const history = useHistory();
  const { reference } = useParams();
  const proposedMarginToBeRefunded = refundProposal?.breakdown?.hotelMargin?.amount ?? 0;
  const defaultMarginCancellationFee = isDotwBooking
    ? proposedMarginToBeRefunded
    : Number(new Decimal(margin).minus(proposedMarginToBeRefunded));
  const hotelPrice = Number(new Decimal(price).minus(margin));
  const hotelFee = isDotwBooking
    ? refundProposal.breakdown.hotel.amount ?? 0
    : canBeCancelledOnThirdParty
    ? Number(new Decimal(hotelPrice).minus(refundProposal.breakdown.hotel.amount))
    : 0;

  const newHotelPrice = jacTravelTotalPrice;

  const newCostOfHotel = jacTravelTotalPrice - jacTravelTotalCommission;
  const newMarginAmount = jacTravelTotalCommission;

  const { register, handleSubmit, formState, watch, reset } = useForm({
    mode: 'onChange',
    defaultValues: {
      totalPrice: price,
      price: hotelPrice,
      marginCancellationFee: defaultMarginCancellationFee,
      fee: hotelFee
    }
  });

  useEffect(() => {
    if (isAlreadyCancelled && penaltiesApplied) {
      reset((prev) => ({
        ...prev,
        marginCancellationFee: Number(penaltiesApplied.margin),
        fee: Number(penaltiesApplied.value),
        totalPrice: Number(price),
        price: Number(hotelPrice)
      }));
    }
  }, [hotelPrice, isAlreadyCancelled, penaltiesApplied, price, reset]);

  const { errors } = formState;

  const [operation, setOperation] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const toggleModal = () => setIsModalVisible(!isModalVisible);

  const hasErrors = Object.keys(errors).length > 0;
  const isCancelling = operation === OPERATION_TYPE.CANCEL;
  const isExchanging = operation === OPERATION_TYPE.EXCHANGE;

  const shouldDisplayMarginCancellationFee = !isPackage && canBeCancelledOnThirdParty && isCancelling;

  const watchFee = watch('fee');
  const watchPrice = watch('price');
  const watchTotalPrice = watch('totalPrice');
  const watchMarginCancellationFee = watch('marginCancellationFee');

  const getMarginFee = () => {
    if (shouldDisplayMarginCancellationFee) {
      return Number(watchMarginCancellationFee);
    }
  };

  const calculateTotal = () => {
    const currentFee = Number(watchFee) || 0;
    const currentPrice = Number(watchPrice);
    const totalPrice = Number(watchTotalPrice);
    const currentMarginFee = Number(getMarginFee());

    if (isCancelling) {
      const marginFee = shouldDisplayMarginCancellationFee ? margin - currentMarginFee : 0;

      if (isDotwBooking && isAlreadyCancelled) {
        return totalPrice - penaltiesApplied.value - penaltiesApplied.margin;
      }
      if (isDotwBooking && !isAlreadyCancelled) {
        return (
          refundProposal.breakdown.refund.amount -
          (currentFee - refundProposal.breakdown.hotel.amount) -
          (currentMarginFee - refundProposal.breakdown.hotelMargin.amount)
        );
      }

      if (!isDotwBooking) {
        return currentPrice - currentFee + marginFee;
      }
    }

    if (isExchanging) {
      return newHotelPrice - price;
    }

    return 0;
  };

  const handleCancelClick = () => {
    setOperation(OPERATION_TYPE.CANCEL);
    toggleModal();
  };

  const handleExchangeClick = () => {
    setOperation(OPERATION_TYPE.EXCHANGE);
    toggleModal();
  };

  const handleCompensateClick = () => {
    history.push(`/manage/bookings/${reference}/compensate-hotel`);
  };

  const handleOnSubmit = async (formData) => {
    if (isCancelling) {
      await onCancel({ fee: Number(formData.fee), marginFee: getMarginFee() });
    }

    if (isExchanging) {
      await onExchange({
        fee: Number(formData.fee) || 0,
        price: Number(newCostOfHotel),
        margin: newMarginAmount,
        hotelId: formData.hotelId,
        checkInDate: formData.checkInDate,
        jacTravelRef: formData.jacTravelRef
      });
    }

    toggleModal();
  };

  return (
    <>
      <Modal
        isOpen={isModalVisible}
        title={
          <FormattedMessage
            id="extras.hotel.modal.title"
            values={{
              operation
            }}
          />
        }
        onClose={toggleModal}
      >
        <form onSubmit={handleSubmit(handleOnSubmit)} data-cy="hotel-info-form" data-testid="hotel-info-form">
          <StyledInputContainer>
            <StyledInputWrapper>
              <label htmlFor="checkInDate">
                <StyledLabel>
                  <FormattedMessage id="extras.hotel.modal.checkInDate" />
                </StyledLabel>
                <input
                  type="date"
                  data-cy="checkInDate"
                  data-testid="checkInDate"
                  defaultValue={date}
                  disabled
                  {...register('checkInDate', { required: false, value: date })}
                />
              </label>
            </StyledInputWrapper>

            <StyledInputWrapper>
              <label htmlFor="jacTravelRef">
                <StyledLabel>
                  <FormattedMessage id="extras.hotel.modal.jacTravelRef" />
                </StyledLabel>
                <input
                  type="text"
                  data-cy="jacTravelRef"
                  data-testid="jacTravelRef"
                  defaultValue={jacTravelRef}
                  disabled
                  {...register('jacTravelRef', { required: false, value: jacTravelRef })}
                />
              </label>
            </StyledInputWrapper>

            <StyledInputWrapper>
              <label htmlFor="hotelId">
                <StyledLabel>
                  <FormattedMessage id="extras.hotel.modal.hotelId" />
                </StyledLabel>
                <input
                  type="text"
                  data-cy="hotelId"
                  data-testid="hotelId"
                  defaultValue={hotelId}
                  disabled
                  {...register('hotelId', { required: false, value: hotelId })}
                />
              </label>
            </StyledInputWrapper>
            {operation === OPERATION_TYPE.EXCHANGE && (
              <>
                <StyledInputWrapper>
                  <label htmlFor="oldHotelPrice">
                    <StyledLabel>
                      <FormattedMessage id="extras.hotel.modal.oldHotelPrice" />
                    </StyledLabel>
                    <input
                      type="text"
                      data-cy="oldHotelPrice"
                      data-testid="oldHotelPrice"
                      defaultValue={price}
                      disabled
                      {...register('oldHotelPrice', { required: false, value: price })}
                    />
                  </label>
                </StyledInputWrapper>
                <StyledInputWrapper>
                  <label htmlFor="newHotelPrice">
                    <StyledLabel>
                      <FormattedMessage id="extras.hotel.modal.newHotelPrice" />
                    </StyledLabel>
                    <input
                      type="text"
                      data-cy="newHotelPrice"
                      data-testid="newHotelPrice"
                      defaultValue={newHotelPrice}
                      disabled
                      {...register('newHotelPrice', { required: false, value: newHotelPrice })}
                    />
                  </label>
                </StyledInputWrapper>
              </>
            )}

            {operation === OPERATION_TYPE.CANCEL && (
              <>
                <StyledInputWrapper>
                  <StyledLabel>
                    <FormattedMessage id="extras.hotel.modal.price" />
                  </StyledLabel>

                  <CurrencyInput
                    currency={currency}
                    dataCy="price"
                    dataTestId="price"
                    isControlled={false}
                    defaultValue={price}
                    disabled={true}
                    {...register('price', {
                      required: false,
                      value: price
                    })}
                  />
                </StyledInputWrapper>
                <StyledInputWrapper>
                  <StyledLabel>
                    <FormattedMessage id="extras.hotel.modal.fee" values={{ operation }} />
                  </StyledLabel>
                  <CurrencyInput
                    currency={currency}
                    dataCy="fee"
                    dataTestId="fee"
                    isControlled={false}
                    {...register('fee', {
                      required: true,
                      min: 0.0,
                      max: watchPrice
                    })}
                    step={0.01}
                  />
                </StyledInputWrapper>
                <StyledInputWrapper>
                  <StyledLabel>
                    <FormattedMessage id="extras.hotel.margin" />
                  </StyledLabel>
                  <StyledCurrencyValue>
                    <FormattedCurrency amount={margin} currency={currency} />
                  </StyledCurrencyValue>
                </StyledInputWrapper>
              </>
            )}

            {shouldDisplayMarginCancellationFee && (
              <StyledInputWrapper>
                <StyledLabel>
                  <FormattedMessage id="extras.hotel.modal.marginCancellationFee" />
                </StyledLabel>
                <CurrencyInput
                  currency={currency}
                  dataCy="marginCancellationFee"
                  dataTestId="marginCancellationFee"
                  isControlled={false}
                  {...register('marginCancellationFee', {
                    required: shouldDisplayMarginCancellationFee,
                    min: 0.0,
                    max: margin
                  })}
                  step={0.01}
                />
              </StyledInputWrapper>
            )}
          </StyledInputContainer>

          {refundProposal && (
            <AlertBox variant={AlertBox.variants.WARNING}>
              <FormattedMessage id="extras.hotel.modal.feeMessage" />
            </AlertBox>
          )}

          {!canBeCancelledOnThirdParty && (
            <AlertBox variant={AlertBox.variants.WARNING}>
              <FormattedMessage id="extras.hotel.modal.refundThirdParty" />
            </AlertBox>
          )}

          {refundProposalError && (
            <AlertBox variant={AlertBox.variants.ERROR}>
              <FormattedMessage id="extras.hotel.modal.refundProposalError" />
            </AlertBox>
          )}

          <StyledBreakdown>
            <StyledTotalContainer data-cy="total">
              <FormattedMessage id="extras.hotel.modal.total" values={{ operation }} />:
              <FormattedCurrency currency={currency} amount={calculateTotal()} />
            </StyledTotalContainer>
            {isExchanging && (
              <>
                <StyledBreakdownRow data-testid="original-cost-of-hotel">
                  <FormattedMessage id="extras.hotel.modal.originalHotel" /> <FormattedCurrency amount={hotelPrice} currency={currency} />
                </StyledBreakdownRow>
                {canBeCancelledOnThirdParty && (
                  <StyledBreakdownRow data-testid="original-margin-amount">
                    <FormattedMessage id="extras.hotel.modal.originalMargin" /> <FormattedCurrency amount={margin} currency={currency} />
                  </StyledBreakdownRow>
                )}
                <StyledBreakdownRow data-testid="new-cost-of-hotel">
                  <FormattedMessage id="extras.hotel.modal.newHotelCost" />{' '}
                  <FormattedCurrency amount={newCostOfHotel} currency={currency} />
                </StyledBreakdownRow>
                <StyledBreakdownRow data-testid="new-margin-amount">
                  <FormattedMessage id="extras.hotel.modal.newMargin" /> <FormattedCurrency amount={newMarginAmount} currency={currency} />
                </StyledBreakdownRow>
              </>
            )}
          </StyledBreakdown>

          <StyledFormFooter>
            <StyledButtonGroup>
              <ConfirmButton type="submit" data-testid="submit" data-cy="submit" disabled={loading} variant="primary">
                <FormattedMessage id="extras.hotel.modal.confirm" />
              </ConfirmButton>

              <ActionButton type="button" data-testid="close" onClick={toggleModal} disabled={loading}>
                <FormattedMessage id="extras.hotel.modal.close" />
              </ActionButton>
            </StyledButtonGroup>

            {operation === OPERATION_TYPE.CANCEL && (
              <StyledWarningMessage>
                <FormattedMessage id="extras.hotel.modal.warning" />
              </StyledWarningMessage>
            )}
          </StyledFormFooter>
          {hasErrors && (
            <StyledErrorMessage data-testid="errors">
              <FormattedMessage id="extras.hotel.modal.error" />
            </StyledErrorMessage>
          )}
        </form>
      </Modal>

      <StyledButtonGroup>
        {canExchange && (
          <ActionButton
            type="button"
            data-testid="exchange"
            data-cy="exchange"
            onClick={handleExchangeClick}
            disabled={!isConfirmed || isCompensated || jacTravelTotalPrice === undefined || jacTravelTotalCommission === undefined}
          >
            <FormattedMessage id="extras.hotel.modal.exchange" />
          </ActionButton>
        )}

        {isHotelOnly && (
          <ActionButton
            type="button"
            data-testid="cancel"
            data-cy="cancel"
            onClick={handleCancelClick}
            disabled={!isConfirmed || isCompensated}
          >
            <FormattedMessage id="extras.hotel.modal.cancel" />
          </ActionButton>
        )}

        {enableHotelCompensation && canCompensate && (
          <ActionButton
            type="button"
            data-testid="compensate"
            data-cy="compensate"
            onClick={handleCompensateClick}
            disabled={!isConfirmed || isCompensationDisabled || isCompensated}
          >
            <FormattedMessage id="extras.hotel.modal.compensate" />
          </ActionButton>
        )}

        {isCompensated && (
          <div data-cy="is-compensated-message" style={{ color: 'red' }}>
            <FormattedMessage id="extras.hotel.isCompensated" />
          </div>
        )}
      </StyledButtonGroup>
    </>
  );
};

ManageHotel.propTypes = {
  canBeCancelledOnThirdParty: PropTypes.bool,
  date: PropTypes.string.isRequired,
  jacTravelRef: PropTypes.string.isRequired,
  jacTravelTotalPrice: PropTypes.number,
  jacTravelTotalCommission: PropTypes.number,
  hotelId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  price: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  isConfirmed: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onExchange: PropTypes.func,
  canExchange: PropTypes.bool,
  canCompensate: PropTypes.bool,
  isCompensationDisabled: PropTypes.bool,
  refundProposal: PropTypes.shape({
    value: PropTypes.shape({
      amount: PropTypes.number
    }),
    breakdown: PropTypes.shape({
      hotelMargin: PropTypes.shape({
        amount: PropTypes.number
      }),
      hotel: PropTypes.shape({
        amount: PropTypes.number
      }),
      refund: PropTypes.shape({
        amount: PropTypes.number
      })
    })
  }),
  isPackage: PropTypes.bool.isRequired,
  isHotelOnly: PropTypes.bool,
  refundProposalError: PropTypes.bool,
  margin: PropTypes.number,
  isCompensated: PropTypes.bool,
  isDotwBooking: PropTypes.bool,
  isAlreadyCancelled: PropTypes.bool,
  penaltiesApplied: PropTypes.shape({
    value: PropTypes.number,
    margin: PropTypes.number
  })
};

ManageHotel.defaultProps = {
  canCompensate: true,
  canBeCancelledOnThirdParty: false
};

export default ManageHotel;
